import React, { useEffect, useState, useContext } from "react";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import * as XLSX from "xlsx";
import './styles/MasterSiteManager.css';
import MapViewer from './MapViewer';
import { AuthContext } from '../context/AuthContext';

export default function MasterSiteManager({ refreshKey }) {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  const token = localStorage.getItem('token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const [masterSites, setMasterSites] = useState([]);
  const [form, setForm] = useState({
    name: "",
    dispenserType: "",
    dailyDataAvailableFrom: "",
    dailyDataAvailableUpto: "now",
  });
  const [editingId, setEditingId] = useState(null);
  const [siteMap, setSiteMap] = useState({});
  const [reportMap, setReportMap] = useState({});
  const [reportDate, setReportDate] = useState("");
  const [dispenserTypes, setDispenserTypes] = useState([]);

  const parseDecimal = (value) => {
    if (typeof value === "object" && value.$numberDecimal) return parseFloat(value.$numberDecimal);
    return parseFloat(value || 0);
  };

  const fetchMasterSites = async () => {
    const res = await axios.get('/api/mastersites', authHeader);
    setMasterSites(res.data);
    const siteRes = await axios.get("/api/sites", authHeader);
    const map = {};
    siteRes.data.forEach(site => {
      if (!map[site.masterSiteId]) map[site.masterSiteId] = [];
      map[site.masterSiteId].push(site);
    });
    setSiteMap(map);
  };

  const fetchReportsForDate = async (date) => {
    const allReports = {};
    for (const ms of masterSites) {
      const res = await axios.get(`/api/reports/daily/master/${ms._id}?from=${date}&to=${date}`, authHeader);
      allReports[ms._id] = res.data;
    }
    setReportMap(allReports);
  };

  const exportToExcel = (masterSiteId) => {
    const site = masterSites.find(m => m._id === masterSiteId);
    const reports = reportMap[masterSiteId] || [];
    const rows = reports.map(r => ({
      Site: r.site,
      Phone: r.phoneNumber,
      WaterDispensed_L: parseDecimal(r.totalWaterDispensed),
      AmountCollected_Rs: parseDecimal(r.totalAmountCollected),
    }));
    const total = rows.reduce((acc, row) => {
      acc.WaterDispensed_L += row.WaterDispensed_L;
      acc.AmountCollected_Rs += row.AmountCollected_Rs;
      return acc;
    }, { WaterDispensed_L: 0, AmountCollected_Rs: 0});
    rows.push({ Site: "TOTAL", Phone: "", ...total });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, site.name);
    XLSX.writeFile(wb, `${site.name}_Report_${reportDate}.xlsx`);
  };

  useEffect(() => { fetchMasterSites(); }, [refreshKey]);

  useEffect(() => {
    const fetchDispenserTypes = async () => {
      try {
        const res = await axios.get('/api/dispenserTypes', authHeader);
        setDispenserTypes(res.data);
      } catch (err) {
        console.error("Error fetching dispenser types:", err);
      }
    };
    fetchDispenserTypes();
  }, []);

  useEffect(() => {
    if (reportDate) fetchReportsForDate(reportDate);
  }, [reportDate, masterSites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      await axios.put(`/api/mastersites/${editingId}`, form, authHeader);
    } else {
      await axios.post("/api/mastersites", { ...form, childSites: [] }, authHeader);
    }
    setForm({ name: "", dailyDataAvailableFrom: "", dailyDataAvailableUpto: "now", dispenserType: "" });
    setEditingId(null);
    fetchMasterSites();
  };

  const handleEdit = (site) => {
    setEditingId(site._id);
    setForm({
      name: site.name,
      dailyDataAvailableFrom: site.dailyDataAvailableFrom || "",
      dailyDataAvailableUpto: site.dailyDataAvailableUpto || "now",
      dispenserType: site.dispenserType || ""
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this master site?")) {
      await axios.delete(`/api/mastersites/${id}`, authHeader);
      fetchMasterSites();
    }
  };

  return (
    <div className="master-site-container">
      {isAdmin && (
        <>
          <h2 className="master-site-title">{editingId ? "Edit MasterSite" : "Add MasterSite"}</h2>
          <form onSubmit={handleSubmit} className="master-form">
            <input type="text" placeholder="MasterSite Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input type="text" placeholder="Available From (dd/mm/yyyy)" value={form.dailyDataAvailableFrom} onChange={(e) => setForm({ ...form, dailyDataAvailableFrom: e.target.value })} />
            <input type="text" placeholder="Available Upto" value={form.dailyDataAvailableUpto} onChange={(e) => setForm({ ...form, dailyDataAvailableUpto: e.target.value })} />
            <select value={form.dispenserType || ""} onChange={(e) => setForm({ ...form, dispenserType: e.target.value })}>
              <option value="">Select Dispenser Type</option>
              {dispenserTypes.map((type, idx) => (
                <option key={idx} value={type.dispenserType}>{type.dispenserType}</option>
              ))}
            </select>
            <button className="btn fetch">{editingId ? "Update" : "Add"}</button>
          </form>
        </>
      )}

      <div className="report-date-section">
        <label>Select Report Date:</label>
        <input type="text" className="input-control" placeholder="e.g. 14/05/2025" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
      </div>

      <h2 className="master-title">Existing Master Sites</h2>
      <table className="report-table">
        <thead>
          <tr>
            <th>Master Site</th>
            <th>Sites</th>
            <th>Water Dispensed (L)</th>
            <th>Amount Collected (₹)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {masterSites.map((site) => {
            const reports = reportMap[site._id] || [];
            const totalWater = reports.reduce((sum, r) => sum + parseDecimal(r.totalWaterDispensed), 0);
            const totalAmount = reports.reduce((sum, r) => sum + parseDecimal(r.totalAmountCollected), 0);
            return (
              <React.Fragment key={site._id}>
                <tr>
                  <td>{site.name}</td>
                  <td>
                    <ol className="site-list">
                      {siteMap[site._id]?.map((s, idx) => {
                        const rep = reports.find(r => r.siteId === s._id);
                        return (
                          <li key={s._id}>
                             {s.name} ({s.phoneNumber})<br />
                            💧 {parseDecimal(rep?.totalWaterDispensed)} L | ₹ {parseDecimal(rep?.totalAmountCollected)}
                          </li>
                        );
                      })}
                    </ol>
                  </td>
                  <td>{totalWater.toFixed(2)}</td>
                  <td>{totalAmount.toFixed(2)}</td>
                  <td>
                    {isAdmin && (
                      <>
                        <button className="edit-button" onClick={() => handleEdit(site)}>Edit</button>
                        <button className="delete-button" onClick={() => handleDelete(site._id)}>Delete</button>
                      </>
                    )}
                    <button className="export-button" onClick={() => exportToExcel(site._id)} disabled={!reportDate || !reportMap[site._id]?.length}>Export</button>
                  </td>
                </tr>
                <tr>
                  <td colSpan="5">
                    <strong>📍 Location Map:</strong>
                    <MapViewer sites={siteMap[site._id]?.filter(s => s.latitude && s.longitude)} />
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
